<template>
    <div class="print-bread-list-component">
        <spinner-overlay v-if="loading" class="mt-5" />
        <print-container v-else :title="`Brød- og køreliste ${momentDate.format('DD-MM-YYYY')}`"
            :header_style="{backgroundColor: '#b45f06', color: 'white'}">
            <template v-slot:header>
                <div class="h3">BRØD- OG KØRELISTE - {{ momentDate.format('dddd DD-MM-YYYY') }}</div>
            </template>
            <div class="large-text" style="margin-top: -2em">
                <div v-for="(trip, index) in allTrips" :key="index"
                    :class="{'print-break-after': index < allTrips.length - 1}" class="d-flex align-items-end"
                    style="height: 95vh">
                    <div class="container-fluid">
                        <div class="delimeter"></div>
                        <div class="row">
                            <div class="col-1"><i class="far fa-square"></i></div>
                            <div class="col-5">
                                <span class="single-line-block">{{ trip.driver_name }}</span>
                            </div>
                            <div class="col-3">
                                <span class="single-line-block">{{ trip.vehicle_name }}</span>
                            </div>
                            <div class="col-3">
                                <a :href="`/order_management/orders?tab=delivery&_search[delivery_date_from]=${date}&_search[delivery_date_to]=${date}&_search[delivery_zone]=${trip.delivery_zone_id}`"
                                    class="print-link" target="_blank">
                                    <span v-if="trip.drive_start" class="text-danger single-line-block">Afgang: {{
                                        trip.drive_start
                                        }}</span>
                                    <span v-else class="text-danger single-line-block">Ikke planlagt</span>
                                </a>
                            </div>
                        </div>
                        <div class="delimeter"></div>
                        <template v-for="order, index in trip.orders" :key="order.id">
                            <div class="row">
                                <div class="col-1 text-danger"><span v-if="order.delivery_type === 'warm'">Varm</span>
                                </div>
                                <div class="col-8"><a :href="order.url" class="print-link single-line-block"
                                        target="_blank">{{
                                        order.total_quantity }} x {{ order.productTitle }}</a></div>
                                <div class="col-3">
                                    <div v-if="index === 0"><a :href="order.url" class="print-link single-line"
                                            target="_blank">#{{
                                            order.reference_code }}</a></div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-1"><span></span>
                                </div>
                                <div class="col-11">
                                    <div v-for="extra, index in order.order_products" class="row" :key="index">
                                        <div class="col">
                                            <span class="extra-product-line">{{ extra.quantity }} x {{ extra.product ?
                                                extra.product.name : '' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div class="delimeter"></div>
                    </div>
                </div>
            </div>
        </print-container>
    </div>
</template>

<script>
import SpinnerOverlay from '@/components/SpinnerOverlay.vue';
import { Order } from '@/models/order';
import PrintContainer from '@/print/PrintContainer.vue';
import { useRouteDataStore } from '@/stores/route-data';
import axios from 'axios';
import moment from 'moment/moment';
import { computed, ref } from 'vue';

export default {
  components: { PrintContainer, SpinnerOverlay },
  props: {
    delivery_zones: {
      type: Array,
      required: true
    },
    vehicles: {
      type: Array,
      required: true
    },
    drivers: {
      type: Array,
      required: true
    },
  },
  setup (props) {
    const date = computed(() => {
      return new URLSearchParams(window.location.search).get('date');
    });
    const momentDate = computed(() => {
      moment.locale('da');
      return moment(date.value);
    });

    const loading = ref(true);
    const routeDataStore = useRouteDataStore();

    const allTrips = ref([]);
    
    const tripNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    const getTrips = (orders) => {
      const ordersByTrips = {none: [], A: [], B: [], C: [], D: [], E: [], F: [], G: []};
      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        if (order.route_code) {
          ordersByTrips[order.route_code].push(order);
        } else {
          ordersByTrips.none.push(order);
        }
      }
      for (let i = 0; i < tripNames.length; i++) {
        ordersByTrips[tripNames[i]].sort((a, b) => {
          return a.tour_code - b.tour_code;
        });
      }
      ordersByTrips.none.sort((a, b) => {
        return a.deliveryIntervalStart.localeCompare(b.deliveryIntervalStart);
      });
      return ordersByTrips;
    };

    const loadOrders = async () => {
      // loop delivery_zones and get orders
      for (const deliveryZone of props.delivery_zones) {
        await axios
          .get(`/order_management/orders/orders_data?type=deliveries&date=${date.value}&delivery_zone=${deliveryZone.id}`)
          .then(response => {
            const orders = response.data.orders.map(order => {
              return Order.createReactive(order);
            });
            routeDataStore.setRouteData(response.data.route_data);

            //loop orders and add warm orders to allTrips
            const trips = getTrips(orders);
            for (const tripName of tripNames) {
              const orders = trips[tripName].filter(order => order.route_code === tripName);
              // console.log(deliveryZone.title, tripName, trips[tripName].length, warmOrders.length);
              if (orders.length > 0) {
                const firstOrder = trips[tripName][0];
                allTrips.value.push({
                  drive_start: response.data.route_data === null ? null : firstOrder.driveStart,
                  driver_name: firstOrder.driver_id ? props.drivers.find(driver => driver.id === firstOrder.driver_id).name : '-',
                  vehicle_name: firstOrder.vehicle_id ? props.vehicles.find(vehicle => vehicle.id === firstOrder.vehicle_id).title : '',
                  delivery_zone: deliveryZone.title,
                  delivery_zone_id: deliveryZone.id,
                  trip_name: tripName,
                  orders: orders,
                });
              }
            }
          });
      }
      allTrips.value.sort((a, b) => {
        if (a.drive_start === b.drive_start) {
          return 0;
        } else if (!a.drive_start) {
          return -1;
        } else if (!b.drive_start) {
          return 1;
        } else {
          return a.drive_start.localeCompare(b.drive_start);
        }
      });
      loading.value = false;
      console.log(allTrips.value);
      setTimeout(window.print, 250);
    };
    loadOrders();
    return {
      date,
      momentDate,
      allTrips,
      loading,
    }
  }
}
</script>

<style lang="scss" scoped>
.print-bread-list-component {
  .delimeter {
    margin: 8px 0;
    border-top: 0.25mm solid #d0d0d0;
  }
  .text-transparent {
    color: transparent;
  }
}
</style>